/*
 * All Metals — "closest standard size" matcher (theme-side correction)
 * ---------------------------------------------------------------------------
 * Cut-to-size sheet/plate products use two independent apps on the same page:
 *
 *   1. DPO / Itoris ("Dynamic Product Options")  -> renders the Custom Width and
 *      Custom Length dropdowns (.width select / .len select) and attaches them
 *      to the order as line-item properties.
 *   2. bcpo / Variant Options Product Options    -> renders the "Stock Size"
 *      radio list (.bcpo-front-dd-option), which is what actually selects the
 *      Shopify variant used for price, weight and shipping.
 *
 * DPO's option template carries an "Extra JS" snippet that is supposed to bridge
 * the two: read the entered dimensions, then click the matching Stock Size radio.
 * That snippet has two defects:
 *
 *   A. It matches on AREA only ( width * length ), not on whether the piece
 *      actually fits inside the stock sheet. A 20" x 20" piece (400 sq in) is
 *      matched to a 12" x 36" sheet (432 sq in) — which it cannot be cut from,
 *      and which is priced $7.51 lower than the 24" x 24" sheet it really needs.
 *
 *   B. If the Stock Size radios are not in the DOM at the moment the shopper
 *      chooses a dimension (the two apps load independently, so this is a race),
 *      the loop matches nothing and exits silently. The variant is left on the
 *      product's first stock size — 6" x 6" — while the entered dimensions still
 *      ride along as line-item properties. That is the "cart should have
 *      triggered 24x30, instead it triggered 6x6" report.
 *
 * This file re-does the match correctly and never fails silently: it waits for
 * both apps, picks the smallest stock size the piece genuinely fits inside
 * (either orientation), and re-runs whenever either app re-renders.
 *
 * The permanent home for this logic is the DPO option template's "Extra JS"
 * field (template 77998) — the code below is deliberately dependency-free so it
 * can be pasted there verbatim once the app side is ready to be edited.
 */
(function () {
  'use strict';

  var DEBUG = /[?&]amdebug=1/.test(window.location.search);
  var log = function () {
    if (DEBUG && window.console) console.log.apply(console, ['[am-size-matcher]'].concat([].slice.call(arguments)));
  };

  var SEL_WIDTH = '.width input';
  var SEL_LENGTH = '.len input';
  var SEL_STOCK = '.bcpo-front-dd-option';

  /* "24\" (24.00\")" -> 24 ; "24\"-1/2\" (24.5\")" -> 24.5 */
  function readDimension(input) {
    if (!input) return NaN;
    var value = String(input.value || '');
    var parenthesised = value.match(/\(\s*([0-9]*\.?[0-9]+)/);
    if (parenthesised) return parseFloat(parenthesised[1]);
    var leading = value.match(/([0-9]*\.?[0-9]+)/);
    return leading ? parseFloat(leading[1]) : NaN;
  }

  /* "24\" x 30\", you will receive:" -> { w: 24, l: 30 } */
  function readStockSize(radio) {
    var match = String(radio.value || '').match(/([0-9]*\.?[0-9]+)\s*"?\s*x\s*([0-9]*\.?[0-9]+)/i);
    if (!match) return null;
    return { w: parseFloat(match[1]), l: parseFloat(match[2]), el: radio };
  }

  /* A piece fits a sheet if it fits in either orientation. */
  function fits(pieceW, pieceL, sheet) {
    return (pieceW <= sheet.w && pieceL <= sheet.l) || (pieceW <= sheet.l && pieceL <= sheet.w);
  }

  function chooseStockSize(pieceW, pieceL, sheets) {
    var best = null;
    for (var i = 0; i < sheets.length; i++) {
      if (!fits(pieceW, pieceL, sheets[i])) continue;
      var area = sheets[i].w * sheets[i].l;
      if (!best || area < best.area) best = { area: area, sheet: sheets[i] };
    }
    /* Nothing large enough: fall back to the largest sheet on offer rather than
       leaving the shopper on whatever was selected before. */
    if (!best && sheets.length) {
      var largest = sheets[0];
      for (var j = 1; j < sheets.length; j++) {
        if (sheets[j].w * sheets[j].l > largest.w * largest.l) largest = sheets[j];
      }
      return { sheet: largest, oversize: true };
    }
    return best ? { sheet: best.sheet, oversize: false } : null;
  }

  function applyMatch(reason) {
    var widthInput = document.querySelector(SEL_WIDTH);
    var lengthInput = document.querySelector(SEL_LENGTH);
    if (!widthInput || !lengthInput) return false;

    var pieceW = readDimension(widthInput);
    var pieceL = readDimension(lengthInput);
    if (!isFinite(pieceW) || !isFinite(pieceL) || pieceW <= 0 || pieceL <= 0) {
      log('dimensions not ready', widthInput.value, lengthInput.value);
      return false;
    }

    var radios = [].slice.call(document.querySelectorAll(SEL_STOCK));
    var sheets = [];
    for (var i = 0; i < radios.length; i++) {
      var sheet = readStockSize(radios[i]);
      if (sheet) sheets.push(sheet);
    }
    if (!sheets.length) {
      /* The stock-size list has not rendered yet. Do NOT give up quietly —
         retrying here is the whole point of this file. */
      log('stock sizes not in DOM yet, will retry', reason);
      return false;
    }

    var choice = chooseStockSize(pieceW, pieceL, sheets);
    if (!choice) return false;

    if (choice.oversize) log('no stock size fits', pieceW, pieceL, '- falling back to largest');

    if (!choice.sheet.el.checked) {
      log(reason, pieceW + '" x ' + pieceL + '" ->', choice.sheet.el.value);
      choice.sheet.el.click();
      if (!choice.sheet.el.checked) {
        choice.sheet.el.checked = true;
        choice.sheet.el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    return true;
  }

  /* Retry until both apps have rendered. Backs off, then keeps a slow heartbeat
     so a late re-render by either app still gets corrected. */
  function applyWithRetry(reason) {
    var attempts = 0;
    (function attempt() {
      if (applyMatch(reason)) return;
      if (++attempts > 40) return log('gave up after', attempts, 'attempts:', reason);
      setTimeout(attempt, attempts < 10 ? 150 : 500);
    })();
  }

  var pending = null;
  function schedule(reason) {
    clearTimeout(pending);
    pending = setTimeout(function () {
      applyWithRetry(reason);
    }, 30);
  }

  /* React to the shopper changing either dimension. Bound on the document so it
     survives DPO tearing down and rebuilding its fields. */
  document.addEventListener(
    'change',
    function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (el.closest('.width') || el.closest('.len')) schedule('dimension changed');
    },
    true
  );

  /* React to either app rendering (or re-rendering) its controls. */
  var observerScheduled = false;
  new MutationObserver(function () {
    if (observerScheduled) return;
    observerScheduled = true;
    setTimeout(function () {
      observerScheduled = false;
      if (document.querySelector(SEL_WIDTH) && document.querySelector(SEL_STOCK)) schedule('dom changed');
    }, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      schedule('initial');
    });
  } else {
    schedule('initial');
  }
})();
